const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string | undefined;

// Loop-break for #130: federated users with no tenant assigned previously hit
// /alerts → 401 → /login → (Cognito session valid) → /app → 401, repeating.
// We track the timestamp of the last /login redirect; a second 401 within the
// window means the user has a valid session that the API consistently rejects,
// so we route to /no-tenant instead of looping back through /login.
const LOGIN_REDIRECT_KEY = "secops_last_login_redirect";
const LOGIN_REDIRECT_LOOP_MS = 30_000;

type GetTokenFn = () => Promise<string | null>;
type GetImpersonationFn = () => string | null;

let _getToken: GetTokenFn = async () => null;
let _getImpersonation: GetImpersonationFn = () => null;

/** Register the token getter — called once by AuthProvider. */
export function registerTokenGetter(fn: GetTokenFn) {
  _getToken = fn;
}

/** Register the impersonation getter — called by ImpersonationProvider. */
export function registerImpersonationGetter(fn: GetImpersonationFn) {
  _getImpersonation = fn;
}

export interface ApiOpts {
  targetTenantId?: string;
}

/**
 * Error thrown for non-OK HTTP responses (other than the 401/no-tenant
 * redirects, which navigate away). Carries the HTTP `status` so callers can
 * render code-specific messages. Network/timeout failures surface as the
 * native `TypeError` from `fetch` and are NOT `ApiError` (no status exists) —
 * consumers should treat a non-`ApiError` rejection as a connectivity problem.
 *
 * Extends `Error` and preserves `.message` exactly, so existing
 * `catch`/`instanceof Error`/`err.message` callers are unaffected.
 */
export class ApiError extends Error {
  readonly status?: number;
  readonly detail?: string;
  /** Machine-readable error code from the body's `error` field, when present. */
  readonly code?: string;

  constructor(message: string, status?: number, detail?: string, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
    this.code = code;
  }
}

/** Authenticated fetch wrapper. Redirects to /login on 401. */
export async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
  opts?: ApiOpts,
): Promise<T> {
  const token = await _getToken();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string> | undefined),
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // Explicit targetTenantId takes precedence
  if (opts?.targetTenantId) {
    headers["X-Target-Tenant-Id"] = opts.targetTenantId;
  } else if (!path.startsWith("/api/superadmin")) {
    // Auto-inject impersonation for tenant-scoped routes only
    const impersonatedTenant = _getImpersonation();
    if (impersonatedTenant) {
      headers["X-Target-Tenant-Id"] = impersonatedTenant;
    }
  }

  const url = API_BASE_URL ? `${API_BASE_URL}${path}` : path;

  const res = await fetch(url, {
    ...options,
    headers,
  });

  if (res.status === 401) {
    const last = sessionStorage.getItem(LOGIN_REDIRECT_KEY);
    const now = Date.now();
    if (last && now - parseInt(last, 10) < LOGIN_REDIRECT_LOOP_MS) {
      // Second 401 within the loop window — Cognito session is valid but the
      // API consistently rejects it. Surface a clear error instead of looping.
      sessionStorage.removeItem(LOGIN_REDIRECT_KEY);
      window.location.href = "/no-tenant";
      throw new Error("Authentication loop detected — no tenant assigned");
    }
    sessionStorage.setItem(LOGIN_REDIRECT_KEY, String(now));
    window.location.href = "/login";
    throw new Error("Unauthorized");
  }

  if (res.status === 403) {
    // Server signals "authenticated but no tenant" via a structured 403 with
    // code:"no_tenant" (see src/api/middleware/auth.py:require_tenant). Route
    // straight to /no-tenant — bypassing /login eliminates the redirect loop.
    const text = await res.text();
    let body: { code?: string; message?: string; detail?: string } | null = null;
    try {
      body = JSON.parse(text);
    } catch {
      // not JSON
    }
    if (body?.code === "no_tenant") {
      window.location.href = "/no-tenant";
      throw new Error(body.message || "No tenant assigned");
    }
    throw new ApiError(body?.message || body?.detail || "Forbidden", 403, body?.detail ?? undefined, body?.code);
  }

  if (!res.ok) {
    const body = await res.text();
    let userMessage = `Request failed (${res.status})`;
    let detail: string | undefined;
    let code: string | undefined;
    try {
      const json = JSON.parse(body);
      if (json.message) userMessage = json.message;
      else if (json.detail) userMessage = json.detail;
      detail = json.detail ?? undefined;
      // Machine code lives under `error` (most routes) or `code` (the
      // no-tenant 403 shape); accept either so callers can branch reliably.
      if (typeof json.error === "string") code = json.error;
      else if (typeof json.code === "string") code = json.code;
    } catch {
      // not JSON, use generic message
    }
    throw new ApiError(userMessage, res.status, detail, code);
  }

  return res.json();
}

/** Convenience GET. */
export function apiGet<T>(path: string, params?: Record<string, string>, opts?: ApiOpts): Promise<T> {
  const url = params
    ? `${path}?${new URLSearchParams(params).toString()}`
    : path;
  return apiFetch<T>(url, undefined, opts);
}

/** Convenience POST. */
export function apiPost<T>(path: string, body: unknown, opts?: ApiOpts): Promise<T> {
  return apiFetch<T>(path, {
    method: "POST",
    body: JSON.stringify(body),
  }, opts);
}

/** Convenience PATCH. */
export function apiPatch<T>(path: string, body: unknown, opts?: ApiOpts): Promise<T> {
  return apiFetch<T>(path, {
    method: "PATCH",
    body: JSON.stringify(body),
  }, opts);
}

/** Convenience PUT. */
export function apiPut<T>(path: string, body: unknown, opts?: ApiOpts): Promise<T> {
  return apiFetch<T>(path, {
    method: "PUT",
    body: JSON.stringify(body),
  }, opts);
}

/** Convenience DELETE. */
export function apiDelete<T>(path: string, opts?: ApiOpts): Promise<T> {
  return apiFetch<T>(path, { method: "DELETE" }, opts);
}
